import {
  CalendarDays,
  FileText,
  MessageSquare,
  Phone,
  PhoneIncoming,
  Receipt,
  Sparkles,
  StickyNote,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import type { StatusTone } from "../data-display/StatusBadge";

/**
 * The unified customer timeline. One row per thing that actually happened to
 * a customer, calls, texts, emails, notes, tasks, appointments, estimates,
 * invoices, payments, jobs, read from the `contact_timeline` view so the UI
 * never has to stitch nine queries together (and can't drift from the data).
 */

export interface TimelineEntry {
  id: string;
  workspace_id: string;
  contact_id: number | null;
  kind: string;
  subkind: string | null;
  title: string | null;
  detail: string | null;
  status: string | null;
  occurred_at: string | null;
}

export interface TimelineKindMeta {
  label: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon chip. */
  chip: string;
}

export const TIMELINE_KINDS: Record<string, TimelineKindMeta> = {
  message_out: {
    label: "Message sent",
    icon: MessageSquare,
    chip: "bg-primary/10 text-primary",
  },
  message_in: {
    label: "Message received",
    icon: MessageSquare,
    chip: "bg-emerald-500/10 text-emerald-600",
  },
  call: { label: "Call", icon: Phone, chip: "bg-sky-500/10 text-sky-600" },
  call_ai: {
    label: "AI call",
    icon: Sparkles,
    chip: "bg-violet-500/10 text-violet-600",
  },
  note: {
    label: "Note",
    icon: StickyNote,
    chip: "bg-amber-500/10 text-amber-600",
  },
  task: {
    label: "Task",
    icon: PhoneIncoming,
    chip: "bg-muted text-muted-foreground",
  },
  appointment: {
    label: "Appointment",
    icon: CalendarDays,
    chip: "bg-indigo-500/10 text-indigo-600",
  },
  estimate: {
    label: "Estimate",
    icon: FileText,
    chip: "bg-muted text-muted-foreground",
  },
  invoice: {
    label: "Invoice",
    icon: Receipt,
    chip: "bg-muted text-muted-foreground",
  },
  payment: {
    label: "Payment",
    icon: Wallet,
    chip: "bg-emerald-500/10 text-emerald-600",
  },
  job: { label: "Job", icon: Wrench, chip: "bg-orange-500/10 text-orange-600" },
};

export const timelineMeta = (kind: string): TimelineKindMeta =>
  TIMELINE_KINDS[kind] ?? {
    label: kind,
    icon: StickyNote,
    chip: "bg-muted text-muted-foreground",
  };

/** Filter groups shown above the timeline. */
export interface TimelineFilter {
  value: string;
  label: string;
  kinds: string[];
}

export const TIMELINE_FILTERS: TimelineFilter[] = [
  { value: "all", label: "All", kinds: [] },
  {
    value: "comms",
    label: "Calls & messages",
    kinds: ["message_in", "message_out", "call", "call_ai"],
  },
  { value: "notes", label: "Notes & tasks", kinds: ["note", "task"] },
  {
    value: "work",
    label: "Jobs & appointments",
    kinds: ["job", "appointment"],
  },
  {
    value: "money",
    label: "Billing",
    kinds: ["estimate", "invoice", "payment"],
  },
];

export const TIMELINE_STATUS_TONES: Record<string, StatusTone> = {
  received: "success",
  delivered: "success",
  sent: "info",
  queued: "info",
  scheduled: "info",
  failed: "danger",
  logged: "neutral",
  completed: "success",
  missed: "warning",
  voicemail: "warning",
  open: "neutral",
  done: "success",
  paid: "success",
  overdue: "danger",
};

/** "3:04 PM" for today, "Aug 9" this year, else "Aug 9, 2025". */
export const formatWhen = (value: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

export interface MessageTemplate {
  id: string;
  workspace_id: string;
  name: string;
  channel: "sms" | "email";
  subject: string | null;
  body: string;
  category: string | null;
}

export interface CallRecord {
  id: string;
  contact_id: number | null;
  direction: string;
  kind: string;
  status: string;
  duration_seconds: number | null;
  recording_url: string | null;
  transcript: string | null;
  summary: string | null;
  notes: string | null;
  created_at: string;
}

export const CALL_STATUS_TONES: Record<string, StatusTone> = {
  queued: "info",
  ringing: "info",
  in_progress: "info",
  completed: "success",
  missed: "warning",
  voicemail: "warning",
  failed: "danger",
  cancelled: "neutral",
};

export const formatDuration = (seconds: number | null): string => {
  if (!seconds) return ", ";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

/** Client-side mirror of private.render_sequence_template. */
export const renderTemplate = (
  body: string,
  contact: { first_name?: string | null; last_name?: string | null },
  companyName?: string | null,
): string =>
  (body ?? "")
    .replace(/\{\{first_name\}\}/g, contact.first_name ?? "there")
    .replace(/\{\{last_name\}\}/g, contact.last_name ?? "")
    .replace(/\{\{company\}\}/g, companyName ?? "your company");
